import { Chip, type ChipProps, useTheme } from "@mui/material";
import { FC } from "react";
import { StatusDefinition } from "vmd-types";

import { BADGE_FALLBACK_COLOR } from "../constants/colors";

export type BadgeProps = {
  /** The raw value stored on the entity, e.g. reportingItem.degreeOfCompletion or reportingItem.status. */
  value?: string;
  /** The configuration array to resolve the value's color from (statusDefinition, degreeOfCompletionDefinition, ...). */
  definitions?: StatusDefinition[];
  /** Bypasses the `definitions` lookup, e.g. for the semaphore-category color which isn't in a definitions array. */
  color?: string;
  textColor?: string;
  fallbackColor?: string;
  size?: ChipProps["size"];
};

/** Colored chip for a `StatusDefinition`-backed field (status, degree of completion, category, ...). */
export const Badge: FC<BadgeProps> = ({
  value,
  definitions,
  color: colorOverride,
  textColor = "#fff",
  fallbackColor = BADGE_FALLBACK_COLOR,
  size = "small",
}) => {
  const theme = useTheme();

  if (!value) return null;

  const color =
    colorOverride ??
    definitions?.find((definition) => definition.name === value)?.color ??
    fallbackColor;

  return (
    <Chip
      size={size}
      label={value}
      sx={{
        backgroundColor: color,
        color: textColor,
        fontWeight: theme.typography.fontWeightMedium,
        fontSize: theme.typography.body2.fontSize,
        height: "1.5em",
        px: 1,
        // A literal px string, NOT a bare number: MUI's `sx` already
        // multiplies a unitless `borderRadius` number by
        // `theme.shape.borderRadius` internally, so passing
        // `theme.shape.borderRadius * N` here would get scaled a second
        // time (e.g. with the default `theme.shape.borderRadius = 2`,
        // `theme.shape.borderRadius * 2.5` renders as 10px, not 5px). "8px"
        // reproduces this chip's original rendered radius exactly.
        borderRadius: "8px",
      }}
    />
  );
};
